import { Alert, Autocomplete, Box, Chip, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { integrationKeys, serviceNowCMDBKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import { StyledTextField } from "../../../../components/forms/styles/formFieldStyles";

const ServiceNowCMDBConfigPage = () => {
  const queryClient = useQueryClient();
  const {
    fetchServiceNowCMDBCompanies,
    getServiceNowCMDBConfiguration,
    updateServiceNowCMDBConfiguration,
  } = useAPI();

  const [pendingChanges, setPendingChanges] = useState(null);

  const {
    data: companiesData,
    isLoading: companiesLoading,
    error: companiesError,
  } = useQuery({
    queryKey: serviceNowCMDBKeys.companies(),
    queryFn: async () => {
      const response = await fetchServiceNowCMDBCompanies();
      return response.data.companies;
    },
  });

  const {
    data: configData,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: serviceNowCMDBKeys.configuration(),
    queryFn: async () => {
      const response = await getServiceNowCMDBConfiguration();
      return response.data.selected_company_ids;
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: updateServiceNowCMDBConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceNowCMDBKeys.configuration(),
      });
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
      Toast.success("ServiceNow CMDB configuration updated successfully");
      setPendingChanges(null);
    },
    onError: (error) => {
      if (error.response) {
        Toast.error(
          `Failed to update configuration: ${error.response.data.detail}`
        );
      } else {
        Toast.error("An error occurred while updating configuration");
      }
    },
  });

  const selectedCompanies = pendingChanges ?? configData ?? [];
  const hasChanges = pendingChanges !== null;

  const handleCompanyChange = (event, newValue) => {
    setPendingChanges(newValue.map((company) => company.sys_id));
  };

  const handleSave = () => {
    if (pendingChanges !== null) {
      updateConfigMutation.mutate({ selected_company_ids: pendingChanges });
    }
  };

  if (companiesLoading || configLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <LoadingIcon size={25} />
      </Box>
    );
  }

  if (companiesError || configError) {
    return (
      <Alert severity="error">
        Error loading ServiceNow CMDB configuration. Please try again later.
      </Alert>
    );
  }

  const companyOptions = companiesData || [];
  const selectedCompanyObjects = companyOptions.filter((company) =>
    selectedCompanies.includes(company.sys_id)
  );

  return (
    <Box width={700}>
      <Typography variant="h4" mb={2}>
        Configure ServiceNow CMDB Integration
      </Typography>

      <Typography variant="body2" mb={2}>
        Select which companies to monitor from your ServiceNow CMDB. If no
        companies are selected, data will be collected from all available
        companies.
      </Typography>

      {selectedCompanies.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No companies selected - monitoring all compan
        </Alert>
      ) : (
        <Typography sx={{ mb: 2 }}>
          Currently monitoring assets from {selectedCompanies.length} compan
          {selectedCompanies.length !== 1 ? "ies" : "y"}
        </Typography>
      )}

      <Box mb={3}>
        <Autocomplete
          multiple
          options={companyOptions}
          value={selectedCompanyObjects}
          onChange={handleCompanyChange}
          getOptionLabel={(option) => option.name || ""}
          isOptionEqualToValue={(option, value) =>
            option.sys_id === value.sys_id
          }
          filterSelectedOptions
          renderInput={(params) => (
            <StyledTextField
              {...params}
              label={
                <Typography variant="caption">
                  Select Companies (Based on Stock Symbol)
                </Typography>
              }
              placeholder={
                selectedCompanies.length === 0
                  ? "All companies"
                  : "Add more companies"
              }
              margin="dense"
              fullWidth
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.sys_id}
                label={option.name}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </Box>

      <Box display="flex" justifyContent="flex-start" gap={2}>
        <PrimaryButton
          onClick={handleSave}
          disabled={!hasChanges || updateConfigMutation.isPending}
        >
          {updateConfigMutation.isPending ? "Saving..." : "Save Configuration"}
        </PrimaryButton>
        {hasChanges && (
          <Typography variant="body2" color="text.secondary" alignSelf="center">
            You have unsaved changes
          </Typography>
        )}
      </Box>

      {selectedCompanies.length > 0 && (
        <Box mt={3}>
          <Typography variant="body2" color="text.secondary">
            Currently monitoring {selectedCompanies.length} compan
            {selectedCompanies.length !== 1 ? "ies" : "y"}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ServiceNowCMDBConfigPage;
